<template>
    <div v-if="ui.features.notes.opened" dusk="ReaderNotes" class="flex flex-1 flex-col overflow-hidden">
        <ExportNotesOrHighlight
            :highlights="notesLists"
            :checked-colors="checkedColors"
            @checked-colors="updateCheckedColors"
        />
        <div class="flex-1 overflow-y-auto overflow-x-hidden">
            <!-- A note being written leads the list: the editor is the first thing here. -->
            <NoteEditor v-if="isWritingNewNote" class="border-b border-theme-border" />
            <div
                v-if="!hasRows && !isWritingNewNote"
                role="status"
                aria-live="polite"
                class="flex h-full w-full items-center justify-center px-4 text-center text-theme-text"
            >
                <p class="text-sm opacity-60">{{ emptyMessage }}</p>
            </div>
            <template v-else-if="hasRows">
                <SectionLabel>{{ notesLists.length }} {{ localizations[lang].notes }}</SectionLabel>
                <HighlightAndNotesText :highlights="notesLists" />
            </template>
        </div>
    </div>
</template>

<script>
'use strict';

import { mapGetters, mapMutations, mapState } from 'vuex';
import HighlightAndNotesText from '../partials/HighlightAndNotesText.vue';
import NoteEditor from '../partials/NoteEditor.vue';
import ExportNotesOrHighlight from '../partials/ExportNotesOrHighlight.vue';
import SectionLabel from '../partials/SectionLabel.vue';
import checkedColorsMixin from '_shared_/js/mixins/checkedColorsMixin';

export default {
    name: 'ReaderNotes',
    components: {
        HighlightAndNotesText,
        NoteEditor,
        ExportNotesOrHighlight,
        SectionLabel,
    },
    mixins: [checkedColorsMixin],
    computed: {
        ...mapState(['ui', 'localizations', 'lang', 'selections', 'sharedColors', 'noteFocusUuid']),
        ...mapGetters(['allHighlighters']),

        /**
         * True while a brand-new note is being written: the editor has no note of
         * its own yet, so it sits at the top of the list instead of in a row.
         */
        isWritingNewNote() {
            return this.ui.noteEditor.opened && !this.ui.noteEditor.uuid;
        },

        notesLists() {
            return this.selections.filter(selection => {
                return this.checkedColors.includes(selection.colorSlug) && selection.hasAnnotation;
            });
        },

        hasRows() {
            return this.notesLists.length > 0;
        },

        /**
         * Why the list is empty: no notes at all, or none left after the color filter.
         * @returns {string}
         */
        emptyMessage() {
            const hasNotes = this.selections.some(selection => selection.hasAnnotation);
            return hasNotes ? this.localizations[this.lang].noFilterNotesColor : this.localizations[this.lang].noNotes;
        },
    },
    watch: {
        /**
         * The editor of a stored note can only mount inside that note's row, and a tap
         * on the page can ask for a note whose color the reader unticked. Tick it back,
         * so the row the editor needs is there.
         * @param {string|null} uuid - The note the editor was opened for
         */
        'ui.noteEditor.uuid'(uuid) {
            this.ensureNoteColorIsChecked(uuid);
        },
        /**
         * A note tapped on the page that the reader cannot edit only comes into view here.
         * @param {string|null} uuid - The note to scroll to
         */
        noteFocusUuid(uuid) {
            if (uuid) this.scrollToFocusedNote(uuid);
        },
    },
    methods: {
        ...mapMutations(['setNoteFocusUuid']),
        updateCheckedColors(colors) {
            this.checkedColors = [...colors];
        },
        /**
         * Adds the note's color to the checked filter if it is unticked, so the note's row renders.
         * @param {string|null} uuid - The note whose row must exist
         */
        ensureNoteColorIsChecked(uuid) {
            const note = this.selections.find(selection => selection.uuid === uuid);
            if (!note || this.checkedColors.includes(note.colorSlug)) return;
            this.checkedColors = [...this.checkedColors, note.colorSlug];
        },
        /**
         * Scrolls the list to the note with the given uuid and clears the focus flag,
         * waiting a frame for the (possibly just-opened) panel to render its rows.
         *
         * The optional call is load-bearing: the root is a `v-if`, so a tab closed inside
         * that frame leaves `$el` a comment node, and a throw would strand the flag on this
         * uuid — a second tap would commit no change and never scroll.
         * @param {string} uuid - The target note uuid
         */
        scrollToFocusedNote(uuid) {
            this.ensureNoteColorIsChecked(uuid);
            this.$nextTick(() => {
                requestAnimationFrame(() => {
                    const noteRow = this.$el.querySelector?.(`[data-note-uuid="${uuid}"]`);
                    noteRow?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    this.setNoteFocusUuid(null);
                });
            });
        },
    },
};
</script>
